/**
 * Trace of the join lifecycle — Start clicked, each Zoom SDK event as it
 * arrives, the call widget appearing and being removed, and the end reason
 * chosen. Zoom's own events are unreliable, so this is how an end state gets
 * tied back to whatever produced it.
 *
 * Entries go to `sessionStorage` as well as the console, because the console
 * is lost on the page reloads that happen during a test session.
 *
 * Gated on the same non-prod flag as the Zoom override, so the whole trace is
 * dead-code-eliminated from production bundles.
 */

const STORAGE_KEY = 'zoom.joinTrace';

/** Entries are capped so a long-lived tab cannot fill the storage quota. */
const TRACE_LIMIT = 200;

export function traceJoin(message: string): void {
  if (!import.meta.env.VITE_ZOOM_OVERRIDE_ENABLED) return;

  const line = `${new Date().toISOString().slice(11, 23)} ${message}`;
  console.info(`[join] ${line}`);
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify([...readJoinTrace(), line].slice(-TRACE_LIMIT))
    );
  } catch {
    // Best-effort only — a failed trace must never stop a patient joining.
  }
}

export function readJoinTrace(): string[] {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(stored) ? (stored as string[]) : [];
  } catch {
    return [];
  }
}

export function clearJoinTrace(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — the trace is best-effort.
  }
}
